import { Modal } from "antd";
import { useContext, useRef, useState } from "react";
import "./AttachmentButton.css";
import { isDesktop } from "react-device-detect";
import { useTranslation } from "react-i18next";
import { CheckIcon } from "../../../components/SvgComponents/CheckIcon/CheckIcon";
import { AttachmentIcon } from "../../../components/SvgComponents/AttachmentIcon/AttachmentIcon";
import { ArrowIcon } from "../../../components/SvgComponents/ArrowIcon/ArrowIcon";
import { CancelIcon } from "../../../components/SvgComponents/CancelIcon/CancelIcon";
import { ShowErrorModal } from "../../../components/Modals/ShowErrorModal/ShowErrorModal";
import { InvalidFormatModal } from "../../../components/Modals/InvalidFormatModal/InvalidFormatModal";
import { BatchContext } from "../../../contexts/BatchContext";
import { compressImageIfNeeded } from "../../../util/imageCompression";
import { FILE_SIZE_LIMIT_IN_BYTES, FILE_SIZE_LIMIT_IN_MB } from "../../../util/constants";
import { CircleXIcon } from "../../../components/SvgComponents/CircleXIcon/CircleXIcon";
import { ZoomInIcon } from "../../../components/SvgComponents/ZoomInIcon/ZoomInIcon";
import { ZoomOutIcon } from "../../../components/SvgComponents/ZoomOutIcon/ZoomOutIcon";
import { BannerInfoIcon } from "../../../components/SvgComponents/BannerInfoIcon/BannerInfoIcon";
import jpgIcon from "../../../assets/images/jpg-type.svg";
import pngIcon from "../../../assets/images/png-type.svg";
import heicIcon from "../../../assets/images/heic-type.svg";
import refreshIcon from "../../../assets/svg/refresh.svg";
import completedIcon from "../../../assets/images/completed.png";

const VALID_FORMATS = ['image/jpeg', 'image/jpg', 'image/png', 'image/heic', 'image/heif'];
const VALID_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.heic'];

const isValidFormat = (file) => {
  const type = file.type.toLowerCase();
  const name = file.name.toLowerCase();
  return VALID_FORMATS.includes(type) || VALID_EXTENSIONS.some(ext => name.endsWith(ext));
};

const getFileTypeIcon = (fileName = '') => {
  const name = fileName.toLowerCase();
  if (name.endsWith('.png')) return pngIcon;
  if (name.endsWith('.heic') || name.endsWith('.heif')) return heicIcon;
  return jpgIcon;
};

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

export const AttachmentButton = ({
  attachmentData,
  setAttachmentData,
  buttonText,
}) => {
  const [openModal, setOpenModal] = useState(false);
  const [error, setError] = useState(null);
  const [invalidModalContent, setInvalidModalContent] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [originalFile, setOriginalFile] = useState(null);
  const [isCompressing, setIsCompressing] = useState(false);
  const [compressionProgress, setCompressionProgress] = useState(0);
  const [compressedFile, setCompressedFile] = useState(null);
  const [selectedView, setSelectedView] = useState('compressed');
  const [zoom, setZoom] = useState(1);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);

  const cancelledRef = useRef(false);
  const inputFileRef = useRef(null);
  const isDraggingRef = useRef(false);
  const dragStartRef = useRef({ x: 0, y: 0, ox: 0, oy: 0 });

  const { isBatchMultisignature } = useContext(BatchContext);
  const { t } = useTranslation();

  const startCompression = async (file) => {
    cancelledRef.current = false;
    setIsCompressing(true);
    setCompressionProgress(0);
    setSelectedView('compressed');
    setZoom(1);
    setDragOffset({ x: 0, y: 0 });

    try {
      const compressed = await compressImageIfNeeded(file, {
        onProgress: (p) => {
          if (!cancelledRef.current) setCompressionProgress(p);
        },
      });

      if (cancelledRef.current) return;

      const reader = new FileReader();
      reader.readAsDataURL(compressed);
      reader.onloadend = () => {
        if (cancelledRef.current) return;
        setCompressedFile(compressed);
        setAttachmentData(reader.result);
        setIsCompressing(false);
      };
    } catch {
      if (cancelledRef.current) return;
      setError(t('document_id.image_compression_failed'));
      handleRemoveFile();
    }
  };

  const handleChange = () => {
    const file = inputFileRef.current.files[0];

    if (!isValidFormat(file)) {
      inputFileRef.current.value = null;
      setInvalidModalContent({});
      return;
    }

    if (file.size > FILE_SIZE_LIMIT_IN_BYTES) {
      inputFileRef.current.value = null;
      setInvalidModalContent({
        title: t('document_id.file_size_limit_exceeded'),
        description: `${t('document_id.file_size_limit_exceeded')} (${FILE_SIZE_LIMIT_IN_MB} MB)`,
      });
      return;
    }

    setOriginalFile(file);
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => {
      setPreviewUrl(reader.result);
      setOpenModal(true);
      startCompression(file);
    };
  };

  const handleRemoveFile = () => {
    cancelledRef.current = true;
    isDraggingRef.current = false;
    setAttachmentData(null);
    setPreviewUrl(null);
    setOriginalFile(null);
    setCompressedFile(null);
    setOpenModal(false);
    setIsCompressing(false);
    setCompressionProgress(0);
    setSelectedView('compressed');
    setZoom(1);
    setDragOffset({ x: 0, y: 0 });
    setIsDragging(false);
    inputFileRef.current.value = null;
  };

  const handleLoadAnotherImage = () => {
    handleRemoveFile();
    requestAnimationFrame(() => requestAnimationFrame(() => inputFileRef.current.click()));
  };

  const handleSelectView = (view) => {
    setSelectedView(view);
    setZoom(1);
    setDragOffset({ x: 0, y: 0 });
  };

  const zoomIn = () => setZoom(z => Math.min(parseFloat((z + 0.25).toFixed(2)), 3));
  const zoomOut = () => setZoom(z => {
    const next = Math.max(parseFloat((z - 0.25).toFixed(2)), 1);
    if (next === 1) setDragOffset({ x: 0, y: 0 });
    return next;
  });

  const handlePointerDown = (e) => {
    if (zoom <= 1) return;
    isDraggingRef.current = true;
    setIsDragging(true);
    dragStartRef.current = { x: e.clientX, y: e.clientY, ox: dragOffset.x, oy: dragOffset.y };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e) => {
    if (!isDraggingRef.current) return;
    const dx = e.clientX - dragStartRef.current.x;
    const dy = e.clientY - dragStartRef.current.y;
    setDragOffset({ x: dragStartRef.current.ox + dx, y: dragStartRef.current.oy + dy });
  };

  const handlePointerUp = () => {
    isDraggingRef.current = false;
    setIsDragging(false);
  };

  const displayUrl = selectedView === 'original' ? previewUrl : (attachmentData || previewUrl);
  const isCompleted = !isCompressing && !!attachmentData;
  const modalWidth = isDesktop ? "80%" : "100%";

  return (
    <>
      <button
        onClick={() => inputFileRef.current.click()}
        className={attachmentData ? "attached-button" : ""}
      >
        <span>{buttonText}</span>
        {attachmentData ? <CheckIcon /> : <AttachmentIcon />}
      </button>
      <input
        accept="image/*,.heic,.heif"
        onChange={handleChange}
        type="file"
        ref={inputFileRef}
        id="getFile"
        style={{ display: "none" }}
      />
      <Modal
        title=""
        width={modalWidth}
        footer={null}
        open={openModal}
        className="attached-image-modal"
        style={isDesktop ? {} : {
          top: 0,
          left: 0,
          position: 'absolute',
          minWidth: '100vw',
          minHeight: '100vh'
        }}
        closable={true}
        onCancel={handleRemoveFile}
      >
        <p className="attach-modal-instructions">
          {t('document_id.attachment_description')}
        </p>

        <div className="image-wrapper">
          <div
            className="image-scroll-area"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            style={{ cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
          >
            <img
              className="attachment-img"
              src={displayUrl}
              alt="Attachment preview"
              draggable={false}
              style={{
                transform: `translate(${dragOffset.x}px, ${dragOffset.y}px) scale(${zoom})`,
                transition: isDragging ? 'none' : 'transform 0.2s ease',
              }}
            />
          </div>
          <div className="zoom-controls">
            <button className="zoom-btn" onClick={zoomOut} disabled={zoom <= 1}>
              <ZoomOutIcon />
            </button>
            <button className="zoom-btn" onClick={zoomIn} disabled={zoom >= 3}>
              <ZoomInIcon />
            </button>
          </div>
        </div>

        {isCompressing && (
          <div className="compression-section">
            <p className="compression-subtitle">{t('document_id.compressing_subtitle')}</p>
            <div className="compression-file-info">
              <img src={getFileTypeIcon(originalFile?.name)} alt="file type" className="file-type-icon" />
              <div className="file-details">
                <div className="file-details-header">
                  <span className="file-name">{originalFile?.name}</span>
                  <button className="compression-cancel-btn" onClick={handleRemoveFile}>
                    <CircleXIcon />
                  </button>
                </div>
                <div className="file-details-footer">
                  <span className="file-size">{formatFileSize(originalFile?.size)}</span>
                  <span className="optimising-text">
                    <img src={refreshIcon} alt="" className="spin-icon" />
                    {t('document_id.optimising')}
                  </span>
                  <span className="compression-percentage">{compressionProgress}%</span>
                </div>
                <div className="compression-progress-bar">
                  <div className="compression-progress-fill" style={{ width: `${compressionProgress}%` }} />
                </div>
              </div>
            </div>
          </div>
        )}

        {isCompleted && (
          <div className="completed-section">
            <div className="compression-success-banner">
              <BannerInfoIcon />
              <span>{t('document_id.compression_success_banner')}</span>
            </div>
            <div className="file-cards-container">
              <div
                className={`file-card ${selectedView === 'original' ? 'file-card--selected' : ''}`}
                onClick={() => handleSelectView('original')}
              >
                <img src={getFileTypeIcon(originalFile?.name)} alt="file type" className="file-card-icon" />
                <div className="file-card-info">
                  <span className="file-name">{originalFile?.name}</span>
                  <span className="file-size">
                    {formatFileSize(originalFile?.size)} of {formatFileSize(originalFile?.size)}
                  </span>
                </div>
              </div>
              <div
                className={`file-card file-card--compressed ${selectedView === 'compressed' ? 'file-card--selected' : ''}`}
                onClick={() => handleSelectView('compressed')}
              >
                <img src={getFileTypeIcon(compressedFile?.name)} alt="file type" className="file-card-icon" />
                <div className="file-card-info">
                  <span className="file-name">{compressedFile?.name}</span>
                  <div className="file-size-row">
                    <span className="file-size">
                      {formatFileSize(compressedFile?.size)} of {formatFileSize(compressedFile?.size)}
                    </span>
                    <span className="completed-badge">
                      <img src={completedIcon} alt="completed" className="completed-badge-icon" />
                      {t('document_id.completed')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="modal-buttons-container">
          <button onClick={handleRemoveFile}>
            <span>{t('general.cancel')}</span>
            <CancelIcon />
          </button>
          <button onClick={() => setOpenModal(false)} disabled={isCompressing}>
            <span>{t('general.continue')}</span>
            <ArrowIcon disabled={isCompressing} />
          </button>
        </div>

        {isCompleted && (
          <button className="load-another-btn" onClick={handleLoadAnotherImage}>
            <img src={refreshIcon} alt="" className="load-another-icon" />
            <span>{t('document_id.load_another_image')}</span>
          </button>
        )}
      </Modal>

      <InvalidFormatModal
        open={!!invalidModalContent}
        title={invalidModalContent?.title}
        description={invalidModalContent?.description}
        onCancel={() => setInvalidModalContent(null)}
        onLoadImage={() => {
          setInvalidModalContent(null);
          inputFileRef.current.click();
        }}
      />
      <ShowErrorModal
        openShowErrorModal={error}
        setOpenShowErrorModal={setError}
        errorMessage={error}
        isBatchMultisignature={isBatchMultisignature}
      />
    </>
  );
};
